import { useState, useEffect } from 'react';
import { getFranchises, createFranchise, updateFranchise, deleteFranchise, getFranchiseClients, getAgencies, resendFranchiseCredentials, resendFranchiseCredentialsBulk, lookupUserByEmail, deleteUserByEmail } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Trash2, Users, Search, Pencil, ChevronDown, ChevronRight, Building2, Mail, Phone as PhoneIcon, MapPin, Send, MailCheck, ShieldAlert } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const FranchisesPage = () => {
  const [franchises, setFranchises] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedClients, setExpandedClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: 'changeme123', phone: '', agency_ids: [] });
  const [submitting, setSubmitting] = useState(false);
  const [diagDialogOpen, setDiagDialogOpen] = useState(false);
  const [diagEmail, setDiagEmail] = useState('');
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  useEffect(() => {
    Promise.all([fetchFranchises(), fetchAgencies()]);
  }, []);

  const fetchFranchises = async () => {
    try {
      const res = await getFranchises();
      setFranchises(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      const res = await getAgencies();
      setAgencies(res.data);
    } catch {}
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', email: '', password: 'changeme123', phone: '', agency_ids: [] });
    setDialogOpen(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setFormData({ name: f.name, email: f.email, password: '', phone: f.phone || '', agency_ids: f.agency_ids || [] });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await updateFranchise(editing.id, { name: formData.name, email: formData.email, phone: formData.phone, agency_ids: formData.agency_ids });
        toast.success('Franchisé modifié');
      } else {
        await createFranchise(formData);
        toast.success('Franchisé créé');
      }
      setDialogOpen(false);
      fetchFranchises();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur';
      const isDuplicateEmail = typeof detail === 'string' && detail.toLowerCase().includes('existe déjà');
      if (isDuplicateEmail && !editing) {
        toast.error(detail, {
          description: 'Ouvrez le diagnostic pour voir/supprimer ce compte.',
          action: {
            label: 'Diagnostiquer',
            onClick: () => {
              setDialogOpen(false);
              openDiag(formData.email);
            }
          },
          duration: 10000,
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce franchisé ?')) return;
    try {
      await deleteFranchise(id);
      toast.success('Franchisé supprimé');
      fetchFranchises();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleResend = async (f) => {
    if (!window.confirm(`Renvoyer un nouvel email d'identifiants à ${f.email} ?\n\nUn nouveau mot de passe provisoire sera généré et l'ancien sera invalidé.`)) return;
    try {
      await resendFranchiseCredentials(f.id);
      toast.success(`Email envoyé à ${f.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleResendBulk = async () => {
    if (!window.confirm("Renvoyer un email d'identifiants à TOUS les franchisés qui ne se sont pas encore connectés ?\n\nUn nouveau mot de passe provisoire sera généré pour chacun.")) return;
    try {
      const res = await resendFranchiseCredentialsBulk(true);
      toast.success(res.data.message || 'Emails envoyés');
      if (res.data.failed && res.data.failed.length > 0) {
        toast.warning(`Échecs: ${res.data.failed.join(', ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi groupé');
    }
  };

  const openDiag = (prefillEmail = '') => {
    setDiagEmail(prefillEmail);
    setDiagResult(null);
    setDiagDialogOpen(true);
  };

  const runDiag = async () => {
    if (!diagEmail.trim()) {
      toast.error('Entrez un email');
      return;
    }
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const res = await lookupUserByEmail(diagEmail.trim());
      setDiagResult(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setDiagResult({ notFound: true });
      } else {
        toast.error(error.response?.data?.detail || 'Erreur');
      }
    } finally {
      setDiagLoading(false);
    }
  };

  const handleDeleteByEmail = async () => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT toutes les entrées liées à ${diagEmail} ?\n\nCela supprimera le compte utilisateur ET l'éventuelle fiche client associée.`)) return;
    try {
      const res = await deleteUserByEmail(diagEmail.trim());
      toast.success(res.data.message);
      setDiagResult(null);
      setDiagDialogOpen(false);
      fetchFranchises();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    setLoadingClients(true);
    try {
      const res = await getFranchiseClients(id);
      setExpandedClients(res.data);
    } catch {
      setExpandedClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const toggleAgency = (agencyId) => {
    setFormData(prev => ({
      ...prev,
      agency_ids: prev.agency_ids.includes(agencyId)
        ? prev.agency_ids.filter(id => id !== agencyId)
        : [...prev.agency_ids, agencyId]
    }));
  };

  const filtered = franchises.filter(f =>
    f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (f.agency_names || []).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-5" data-testid="franchises-loading">
        <Skeleton className="h-10 w-64 bg-white" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 bg-white" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="franchises-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#191D1E]">Franchisés</h1>
          <p className="text-sm text-[#687175] mt-1">{franchises.length} licence(s) active(s)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => openDiag()} data-testid="diag-email-btn" className="border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10">
            <ShieldAlert className="w-4 h-4 mr-2" /> Diagnostic email
          </Button>
          <Button variant="outline" onClick={handleResendBulk} data-testid="resend-bulk-btn" className="border-[#397DFF] text-[#397DFF] hover:bg-[#397DFF]/10">
            <MailCheck className="w-4 h-4 mr-2" /> Renvoyer aux non-connectés
          </Button>
          <Button className="btn-primary" onClick={openCreate} data-testid="add-franchise-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau franchisé
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#939BA0]" />
        <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-[#BFC6CC] text-[#191D1E]"
          placeholder="Rechercher un franchisé..." data-testid="franchise-search" />
      </div>

      {/* Franchise list */}
      <div className="space-y-3">
        {filtered.map(f => (
          <motion.div key={f.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-[#BFC6CC]/12 rounded-2xl overflow-hidden" data-testid={`franchise-card-${f.id}`}>
            {/* Main row */}
            <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-4 cursor-pointer flex-1" onClick={() => toggleExpand(f.id)}>
                <div className="w-10 h-10 rounded-lg bg-[#397DFF]/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-[#397DFF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {expandedId === f.id ? <ChevronDown className="w-4 h-4 text-[#687175]" /> : <ChevronRight className="w-4 h-4 text-[#687175]" />}
                    <h3 className="font-bold text-[#191D1E]">{f.name}</h3>
                  </div>
                  <p className="text-xs text-[#687175] mt-0.5 truncate">
                    {f.agency_names?.length > 0 ? f.agency_names.join(', ') : 'Aucune agence'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-center px-3">
                  <p className="text-xl font-bold text-[#191D1E]">{f.client_count}</p>
                  <p className="text-xs text-[#687175]">client(s)</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleResend(f)} className="p-2 text-[#10D183] hover:bg-[#10D183]/10 rounded-lg transition-colors" title="Renvoyer les identifiants" data-testid={`resend-franchise-${f.id}`}>
                    <Send className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(f)} className="p-2 text-[#397DFF] hover:bg-[#397DFF]/10 rounded-lg transition-colors" data-testid={`edit-franchise-${f.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(f.id)} className="p-2 text-[#939BA0] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-franchise-${f.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded clients */}
            <AnimatePresence>
              {expandedId === f.id && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="border-t border-[#BFC6CC]/10 bg-[#F5F6F9]">
                  {loadingClients ? (
                    <div className="p-4"><Skeleton className="h-8 bg-white" /></div>
                  ) : expandedClients.length === 0 ? (
                    <p className="text-sm text-[#687175] p-4">Aucun client créé</p>
                  ) : (
                    <div className="divide-y divide-[#BFC6CC]/10">
                      <div className="grid grid-cols-4 gap-4 px-5 py-2 text-xs font-semibold text-[#687175] uppercase tracking-wider">
                        <span>Nom</span><span>Entreprise</span><span>Email</span><span>Téléphone</span>
                      </div>
                      {expandedClients.map(c => (
                        <div key={c.id} className="grid grid-cols-4 gap-4 px-5 py-3 text-sm text-[#191D1E] bg-white">
                          <span className="font-medium">{c.name}</span>
                          <span className="text-[#687175]">{c.company}</span>
                          <span className="text-[#687175] truncate">{c.email}</span>
                          <span className="text-[#687175]">{c.phone || '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {/* Dialog Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null); }}>
        <DialogContent className="bg-white border-[#BFC6CC]/30 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#191D1E]">
              {editing ? 'Modifier le franchisé' : 'Nouveau franchisé'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#191D1E]">Nom</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="Nom du franchisé" required data-testid="franchise-name-input" />
            </div>
            <div>
              <Label className="text-[#191D1E]">Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="email@exemple.com" required data-testid="franchise-email-input" />
            </div>
            {!editing && (
              <div>
                <Label className="text-[#191D1E]">Mot de passe initial</Label>
                <Input value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="changeme123" data-testid="franchise-password-input" />
              </div>
            )}
            <div>
              <Label className="text-[#191D1E]">Téléphone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="06 12 34 56 78" data-testid="franchise-phone-input" />
            </div>
            <div>
              <Label className="text-[#191D1E] mb-2 block">Agences rattachées</Label>
              <div className="max-h-40 overflow-y-auto border border-[#BFC6CC]/30 rounded-lg p-2 space-y-1">
                {agencies.map(a => (
                  <label key={a.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    formData.agency_ids.includes(a.id) ? 'bg-[#397DFF]/10 text-[#397DFF]' : 'hover:bg-[#F5F6F9] text-[#191D1E]'
                  }`}>
                    <input type="checkbox" checked={formData.agency_ids.includes(a.id)} onChange={() => toggleAgency(a.id)}
                      className="accent-[#397DFF]" />
                    <span className="text-sm">{a.name} — {a.city}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-[#687175] mt-1">{formData.agency_ids.length} agence(s) sélectionnée(s)</p>
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="franchise-submit-btn">
              {submitting ? 'En cours...' : (editing ? 'Enregistrer' : 'Créer le franchisé')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Diagnostic Email Dialog */}
      <Dialog open={diagDialogOpen} onOpenChange={setDiagDialogOpen}>
        <DialogContent className="bg-white border-[#BFC6CC]/30">
          <DialogHeader>
            <DialogTitle className="text-[#191D1E] flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#F59E0B]" />
              Diagnostic email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-[#687175]">
              Recherche un compte existant (franchisé, collaborateur ou client) par email. Utile quand l&apos;email est déjà utilisé et que vous voulez le supprimer pour recréer un compte proprement.
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                value={diagEmail}
                onChange={(e) => setDiagEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runDiag()}
                className="bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]"
                placeholder="email@exemple.com"
                data-testid="diag-email-input"
              />
              <Button onClick={runDiag} disabled={diagLoading} className="btn-primary" data-testid="diag-run-btn">
                {diagLoading ? '...' : 'Rechercher'}
              </Button>
            </div>

            {diagResult && diagResult.notFound && (
              <div className="p-4 bg-[#ECFDF5] border border-[#10D183]/30 rounded-lg">
                <p className="text-sm text-[#10D183] font-semibold">Aucun compte trouvé pour cet email.</p>
                <p className="text-xs text-[#687175] mt-1">Vous pouvez créer le franchisé sans conflit.</p>
              </div>
            )}

            {diagResult && !diagResult.notFound && (
              <div className="space-y-3">
                {diagResult.user && (
                  <div className="p-4 bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-lg space-y-1">
                    <p className="text-xs text-[#B45309] uppercase tracking-wider font-semibold">Utilisateur trouvé</p>
                    <p className="text-sm text-[#191D1E]"><strong>Nom :</strong> {diagResult.user.name || '—'}</p>
                    <p className="text-sm text-[#191D1E]"><strong>Rôle :</strong> {diagResult.user.role_label}</p>
                    {diagResult.user.team_of && <p className="text-xs text-[#687175]">Rattaché à: {diagResult.user.team_of}</p>}
                    <p className="text-xs text-[#687175]">Créé le : {diagResult.user.created_at ? new Date(diagResult.user.created_at).toLocaleString('fr-FR') : '—'}</p>
                    <p className="text-xs text-[#687175]">Mot de passe à changer : {diagResult.user.must_change_password ? 'Oui' : 'Non'}</p>
                  </div>
                )}
                {diagResult.client && (
                  <div className="p-4 bg-[#DBEAFE] border border-[#397DFF]/30 rounded-lg space-y-1">
                    <p className="text-xs text-[#1E40AF] uppercase tracking-wider font-semibold">Fiche client trouvée</p>
                    <p className="text-sm text-[#191D1E]"><strong>Nom :</strong> {diagResult.client.name}</p>
                    <p className="text-sm text-[#191D1E]"><strong>Entreprise :</strong> {diagResult.client.company || '—'}</p>
                    <p className="text-xs text-[#687175]">Formule : {diagResult.client.client_type || '—'}</p>
                  </div>
                )}
                <Button onClick={handleDeleteByEmail} variant="destructive" className="w-full" data-testid="diag-delete-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement cet email
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchisesPage;
